import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Documentation } from 'graphql-common';
import _get from 'lodash/get';
import _valuesIn from 'lodash/valuesIn';
import _without from 'lodash/without';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import Pagination from '@lib/components/Pagination/Pagination';
import useSearchParam from '@lib/hooks/useSearchParam';
import PageOutOfRangeEmptyState from '@lib/components/PageOutOfRangeEmptyState/PageOutOfRangeEmptyState';
import CircleLoaderWrapper, {
  CircleLoaderWrapperType,
} from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import Search from '@lib/components/Search/Search';
import DocumentationPreview from '@lib/components/DocumentationPreview/DocumentationPreview';
import { DocumentationPreviewEnum } from '@lib/components/DocumentationPreview/enums';
import IconButtonWithDropDown from '@lib/components/IconButtonWithDropDown/IconButtonWithDropDown';
import getDocumentActionsMenu from '@lib/components/LinkedDocuments/utils/getDocumentActionsMenu';
import SupportiveMessage from '@lib/components/SupportiveMessage/SupportiveMessage';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import { FieldItem, Value } from '@lib/interfaces/form';
import { DocumentFilterFieldNames } from '../utils/useDocumentFilterFields';
import DocumentsEmptyState from './DocumentsEmptyState';
import DocumentsFilter from './DocumentsFilter';
import styles from './Documents.module.scss';

type Props = {
  collection: Documentation[];
  filterCounter: number;
  filterFields: FieldItem[];
  filterParams: { [key: string]: Value };
  firstLoading: boolean;
  loading: boolean;
  onDetails: (v: Documentation) => void;
  onResetFilter: () => void;
  onToggleFavorite?: (v: Documentation) => void;
  onVersionNotes: (v: Documentation) => void;
  onViewDocument: (v: Documentation) => void;
  setFilterParams: (v: { [key: string]: Value }) => void;
  tabParam?: string;
  totalEntries: number;
};

export default function DocumentList(props: Props) {
  const {
    collection,
    filterCounter,
    filterFields,
    filterParams,
    firstLoading,
    loading,
    onDetails,
    onResetFilter,
    onToggleFavorite,
    onVersionNotes,
    onViewDocument,
    setFilterParams,
    tabParam,
    totalEntries,
  } = props;

  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useSearchParam();
  const [isFilterByQRCodeMessageVisible, setFilterByQRCodeMessageVisible] =
    useState(false);
  const isContentVisible = !!totalEntries && !!collection.length;
  const isEmptyStateVisible = !isContentVisible && !firstLoading;
  const isEmptyStateByOutOfRangePage = isEmptyStateVisible && !!totalEntries;
  const isPaginationVisible =
    !!totalEntries && !!collection.length && totalEntries > collection.length;
  const isSearchApplied = !!searchQuery;
  const isFilterApplied = !!_without(_valuesIn(filterParams), undefined).length;
  const isEmptyState =
    !loading && isEmptyStateVisible && !isSearchApplied && !isFilterApplied;
  const isEmptyBySearch = isEmptyStateVisible && isSearchApplied;
  const isEmptyByFilter = isEmptyStateVisible && isFilterApplied;

  useEffect(() => {
    const areaIds = _get(filterParams, DocumentFilterFieldNames.Areas, []);
    const equipmentIds = _get(
      filterParams,
      DocumentFilterFieldNames.Equipment,
      [],
    );
    setFilterByQRCodeMessageVisible(
      !!(
        (Array.isArray(areaIds) && areaIds.length) ||
        (Array.isArray(equipmentIds) && equipmentIds.length)
      ),
    );
  }, [filterParams]);

  const emptyState = isEmptyStateByOutOfRangePage ? (
    <PageOutOfRangeEmptyState totalEntries={totalEntries} />
  ) : (
    <DocumentsEmptyState
      isEmptyState={isEmptyState}
      isEmptyStateByFilter={isEmptyByFilter}
      isEmptyStateBySearch={isEmptyBySearch}
      onResetFilter={onResetFilter}
      setSearchQuery={setSearchQuery}
      tabParam={tabParam}
    />
  );

  const content = (
    <>
      <div className={styles.documentsGrid}>
        {collection.map((item) => (
          <div key={item.id}>
            <DocumentationPreview
              attachment={item.currentVersion?.attachmentAttached}
              view={DocumentationPreviewEnum.DocumentRowCard}
              customName={item.name}
              isFavorite={!!item.favorited}
              notes={item.currentVersion?.notes || undefined}
              onClick={() => onDetails(item)}
              actions={
                <IconButtonWithDropDown
                  buttonProps={{ icon: 'more_vert' }}
                  popoverProps={{ width: 220, placement: 'bottomRight' }}
                  navItems={getDocumentActionsMenu({
                    item,
                    onDetails,
                    onVersionNotes,
                    onViewDocument,
                    onToggleFavorite,
                    hasDetailsUrl: true,
                    t,
                  })}
                />
              }
            />
          </div>
        ))}
      </div>
      {isPaginationVisible && (
        <div className={styles.pagination}>
          <Pagination totalEntries={totalEntries} />
        </div>
      )}
    </>
  );

  return (
    <div>
      {firstLoading ? (
        <CircleLoader />
      ) : (
        <CircleLoaderWrapper
          isLoading={loading}
          type={CircleLoaderWrapperType.blur}
        >
          {!isEmptyState && (
            <>
              <DocumentsFilter
                filterCounter={filterCounter}
                filterFields={filterFields}
                filterParams={filterParams}
                onResetFilter={onResetFilter}
                setFilterParams={setFilterParams}
              />
              <Search
                placeholder={t('documents-search-placeholder-short')}
                useSearchParam
                usePagination
              />
            </>
          )}
          {isFilterByQRCodeMessageVisible && (
            <SupportiveMessage
              text={t('qr-code-filter-text')}
              className={styles.message}
              actions={
                <IconButton
                  icon="close"
                  type={IconButtonTypes.PrimaryOutlined}
                  onClick={() => setFilterByQRCodeMessageVisible(false)}
                />
              }
            />
          )}
          {isEmptyStateVisible ? emptyState : content}
        </CircleLoaderWrapper>
      )}
    </div>
  );
}
